import React from "react";
import {observer} from "mobx-react-lite";
import {useNavigate} from "react-router-dom";
import {Tag} from "primereact/tag";
import {FileUpload} from "primereact/fileupload";
import appStore from "../../../storage/AppStore";
import clubStore from "../../../storage/ClubStore";
import systemStore from "../../../storage/SystemStore";
import Select from "../../../ui/Select";
import Btn from "../../../ui/Btn";
import PageLayout from "../../PageLayout";
import {getBaseUrl} from "../../../common/config";
import "../../../common/billing.css";

const BillingDetail = ({back_url, store = clubStore.parent}) => {
  const navigate = useNavigate();
  const bill = store.billing_detail || {};
  const cheques = bill.cheques || [];
  const paymentOptions = [
    {id: "cash", name: appStore.tr.cash},
    {id: "transfer", name: appStore.tr.transfer}
  ];
  const title = bill.billing_group?.name || bill.description || bill.billing_type?.name || `#${bill.id || ""}`;
  const status = bill.is_approved
    ? {label: appStore.tr.paid, severity: "success"}
    : bill.is_paid
      ? {label: appStore.tr.awaiting_approval, severity: "warning"}
      : {label: appStore.tr.unpaid, severity: "danger"};
  const canAddPayment = !bill.is_approved;
  const canPay = canAddPayment && !!bill.payment_method && (bill.payment_method !== "transfer" || !!bill.cheque_upload);

  const handlePay = () => {
    store.pay_bill().then(() => {
      systemStore.setSuccess(appStore.tr.successfully_saved);
      navigate(back_url);
    }).catch(() => {});
  };

  return (
    <PageLayout header={
      <div className="flex justify-content-between align-items-center w-full gap-2">
        <div>
          <h3 className="m-0">{appStore.tr.bill_no}: {bill.id}</h3>
          {bill.company_user?.title && <div className="text-600 mt-1">{bill.company_user.title}</div>}
        </div>
        <Btn icon="pi pi-arrow-left" severity="secondary" outlined onClick={() => navigate(back_url)}/>
      </div>
    }>
      <div className="billing-page grid">
        <div className="col-12">
          <div className="game-card billing-header-card">
            <div className="billing-header-main">
              <div className="billing-header-title">{title}</div>
              <div className="billing-header-subtitle">
                {bill.billing_type?.name}
                {bill.object?.name && bill.object.name !== title && <span>, {bill.object.name}</span>}
              </div>
              {bill.billing_group?.period_from && (
                <div className="text-600 text-sm">
                  <i className="pi pi-calendar mr-2"/>
                  {bill.billing_group.period_from}
                  {bill.billing_group.period_to && bill.billing_group.period_to !== bill.billing_group.period_from && <span> — {bill.billing_group.period_to}</span>}
                </div>
              )}
              {bill.notes && <div className="billing-notes">{bill.notes}</div>}
            </div>
            <div className="billing-side">
              <div className="billing-amount">{bill.amount} {bill.currency?.sign || ""}</div>
              <div className="billing-status">
                <Tag severity={status.severity} value={status.label}/>
                {bill.payment_method && <Tag severity="info" value={appStore.tr[bill.payment_method] || bill.payment_method}/>}
              </div>
            </div>
          </div>
        </div>

        {cheques.length > 0 && (
          <div className="col-12">
            <div className="game-card billing-block-card">
              <div className="billing-actions">
                {cheques.map((cheque, index) => (
                  <Btn key={cheque.file_id || index}
                       icon={cheque.mimetype === "application/pdf" ? "pi pi-file-pdf" : "pi pi-image"}
                       label={`${appStore.tr.cheque}${cheques.length > 1 ? ` ${index + 1}` : ""}`}
                       outlined
                       onClick={() => window.open(`${getBaseUrl()}/manager/billing/cheque/${bill.id}/${cheque.file_id}?jwt=${sessionStorage.getItem("jwt")}`, "_blank", "noopener,noreferrer")}/>
                ))}
              </div>
            </div>
          </div>
        )}

        {canAddPayment && (
          <div className="col-12">
            <div className="game-card billing-block-card">
              <div className="grid align-items-end">
                <div className="col-12 md:col-5">
                  <Select store={bill} prop="payment_method" label={appStore.tr.payment_method} options={paymentOptions}/>
                </div>
                {bill.payment_method === "transfer" && (
                  <div className="col-12 md:col-4 billing-upload-wrap">
                    <FileUpload mode="basic" name="cheque_upload" accept="image/*,application/pdf"
                                chooseLabel={appStore.tr.cheque} customUpload
                                onSelect={e => bill.setProperty("cheque_upload", e.files[0])}/>
                  </div>
                )}
                <div className="col-12 md:col-3">
                  <Btn icon="pi pi-check" severity="success" label={appStore.tr.paid} disabled={!canPay} onClick={handlePay}/>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default observer(BillingDetail);